/**
 * AccountLockoutService — Rate-limit failed login attempts
 *
 * Covers TODO Phase 13.2 — Account Lockout:
 *  - 5 failed attempts → 15-minute lockout
 *  - Admin unlock option
 *  - Lockout state persisted in localStorage (server-side in production)
 */

import { logSecurityEvent } from '../../utils/auditLogger';

const LOCKOUT_KEY = 'medfast_lockout';
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes

class AccountLockoutService {
  // ── Record a failed login attempt ─────────────────────────────────

  recordFailure(username) {
    const records = this._getRecords();
    const key = username.toLowerCase();
    const entry = records[key] || { attempts: [], lockedUntil: null };

    entry.attempts.push(Date.now());
    entry.attempts = entry.attempts.filter(t => Date.now() - t < LOCKOUT_DURATION_MS);

    if (entry.attempts.length >= MAX_ATTEMPTS) {
      entry.lockedUntil = Date.now() + LOCKOUT_DURATION_MS;
      logSecurityEvent('account_locked', {
        username: key,
        attempts: entry.attempts.length,
        lockedUntil: new Date(entry.lockedUntil).toISOString(),
      });
    }

    records[key] = entry;
    this._saveRecords(records);
  }

  // ── Check lockout status ──────────────────────────────────────────

  getStatus(username) {
    const records = this._getRecords();
    const key = username.toLowerCase();
    const entry = records[key];

    if (!entry) {
      return { locked: false, failedAttempts: 0, remainingMinutes: 0 };
    }

    if (entry.lockedUntil && Date.now() < entry.lockedUntil) {
      const remaining = Math.ceil((entry.lockedUntil - Date.now()) / 60000);
      return {
        locked: true,
        failedAttempts: entry.attempts.length,
        remainingMinutes: remaining,
        lockedUntil: new Date(entry.lockedUntil).toISOString(),
      };
    }

    if (entry.lockedUntil && Date.now() >= entry.lockedUntil) {
      this.reset(username);
      return { locked: false, failedAttempts: 0, remainingMinutes: 0 };
    }

    const recent = entry.attempts.filter(t => Date.now() - t < LOCKOUT_DURATION_MS);
    return {
      locked: false,
      failedAttempts: recent.length,
      remainingMinutes: 0,
    };
  }

  // ── Reset lockout (login success or admin unlock) ─────────────────

  reset(username) {
    const records = this._getRecords();
    delete records[username.toLowerCase()];
    this._saveRecords(records);
  }

  // ── Admin: unlock any account ─────────────────────────────────────

  adminUnlock(username, adminUserId) {
    this.reset(username);
    logSecurityEvent('admin_unlock_account', {
      username: username.toLowerCase(),
      unlockedBy: adminUserId,
    });
    return { success: true };
  }

  // ── Admin: list all locked accounts ───────────────────────────────

  getLockedAccounts() {
    const records = this._getRecords();
    const locked = [];
    const now = Date.now();

    for (const [username, entry] of Object.entries(records)) {
      if (entry.lockedUntil && now < entry.lockedUntil) {
        locked.push({
          username,
          lockedUntil: new Date(entry.lockedUntil).toISOString(),
          remainingMinutes: Math.ceil((entry.lockedUntil - now) / 60000),
          failedAttempts: entry.attempts.length,
        });
      }
    }
    return locked;
  }

  // ── Persistence ───────────────────────────────────────────────────

  _getRecords() {
    try {
      return JSON.parse(localStorage.getItem(LOCKOUT_KEY) || '{}');
    } catch {
      return {};
    }
  }

  _saveRecords(records) {
    try {
      localStorage.setItem(LOCKOUT_KEY, JSON.stringify(records));
    } catch {
      console.error('[AccountLockout] Failed to persist lockout state');
    }
  }
}

const instance = new AccountLockoutService();
export default instance;
